import React, { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import store from "./redux/store";
import "./index.css";

// ✅ Core layout components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import LoadingScreen from "./components/LoadingScreen";

// ✅ Hooks
import useDynamicPromoIcon from "@/hooks/useDynamicPromoIcon";

// ✅ Service Worker
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

/* ==============================
   💡 LAZY LOAD PAGES FOR PERFORMANCE
================================ */
const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const OrderPlacement = lazy(() => import("./components/purchase/OrderPlacement"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const HelpFAQs = lazy(() => import("./pages/HelpFAQs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// ✅ Shopping
const Shop = lazy(() => import("./pages/shop"));
const Cart = lazy(() => import("./pages/purchase/Cart"));
const Checkout = lazy(() => import("./pages/purchase/Checkout"));
const OrderHistory = lazy(() => import("./pages/purchase/Orders"));
const Payment = lazy(() => import("./pages/purchase/Payment"));
const PaymentSuccess = lazy(() => import("./pages/purchase/PaymentSuccess"));
const PaymentFailure = lazy(() => import("./pages/purchase/PaymentFailure"));
const OrderTracking = lazy(() => import("./pages/purchase/OrderTracking"));
const Wishlist = lazy(() => import("@/pages/purchase/Wishlist"));

// ✅ Auth
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));

// ✅ Admin
const AdminPanel = lazy(() => import("./pages/Admin/AdminPanel"));
const AdminDashboard = lazy(() => import("./pages/Admin/Dashboard"));

/* ==============================
   🔐 PRIVATE & ADMIN ROUTES
================================ */
function PrivateRoute({ children }) {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useSelector((state) => state.auth);
  return user && user.role === "admin" ? children : <Navigate to="/login" replace />;
}

/* ==============================
   🧩 LAYOUT HANDLER
================================ */
function AppLayout() {
  const location = useLocation();
  const promoIcon = useDynamicPromoIcon();

  const AUTH_ROUTES = [
    "/login",
    "/register",
    "/forgot-password",
    "/verify-email",
    "/reset-password",
  ];

  const hideLayout =
    AUTH_ROUTES.some((path) => location.pathname.startsWith(path)) ||
    location.pathname.startsWith("/admin");

  useEffect(() => {
    // Dynamically update favicon based on promotion
    const link = document.querySelector("link[rel='icon']") || document.createElement("link");
    link.rel = "icon";
    link.href = promoIcon;
    document.head.appendChild(link);
  }, [promoIcon]);

  return (
    <>
      {!hideLayout && <Navbar />}

      <Routes>
        {/* ✅ Public Routes */}
        <Route path="/" element={<Home />} />
        {/* 🛍 Shop & Search */}
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/category/:categoryName" element={<Shop />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/product/:id" element={<ProductDetails />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/help" element={<HelpFAQs />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/auth/callback" element={<AuthCallback />} />


        {/* ✅ Protected User Routes */}
        <Route
          path="/checkout/:uuid"
          element={
            <PrivateRoute>
              <Checkout />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <PrivateRoute>
              <OrderHistory />
            </PrivateRoute>
          }
        />
        <Route
          path="/order"
          element={
            <PrivateRoute>
              <OrderPlacement />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path="/affiliate"
          element={
            <PrivateRoute>
              <Affiliate />
            </PrivateRoute>
          }
        />
        <Route
          path="/payment"
          element={
            <PrivateRoute>
              <Payment />
            </PrivateRoute>
          }
        />
        <Route
          path="/wishlist"
          element={
            <PrivateRoute>
              <Wishlist />
            </PrivateRoute>
          }
        />
        <Route
          path="/payment-success"
          element={
            <PrivateRoute>
              <PaymentSuccess />
            </PrivateRoute>
          }
        />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failure" element={<PaymentFailure />} />
        <Route path="/order-tracking/:orderId" element={<OrderTracking />} />

        {/* ✅ Auth Pages */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* ✅ Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />

        {/* ✅ Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {!hideLayout && <Footer />}
    </>
  );
}

/* ==============================
   🌐 APP WRAPPER (with PWA + Suspense)
================================ */
function App() {
  useEffect(() => {
    // Register the PWA service worker
    serviceWorkerRegistration.register({
      onUpdate: (registration) => {
        if (window.confirm("🚀 New update available! Reload to apply?")) {
          registration.waiting?.postMessage({ type: "SKIP_WAITING" });
          window.location.reload();
        }
      },
    });
  }, []);

  return (
    <Provider store={store}>
      <Router>
        <Suspense fallback={<LoadingScreen />}>
        <AppLayout />
        </Suspense>
      </Router>
    </Provider>
  );
}

export default App;
